import React, { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useAuth } from '@sine/auth-client';
import { Loader } from '@sine/sine-ui-components';

import ApplicationFrame from './components/ApplicationFrame';
import OAuthRectifier from './components/OAuthRectifier/OAuthRectifier';
import InsufficientPermissionsPage from './pages/PermissionDenied/InsufficientPermissionsPage';
import { OAUTH_RECTIFIER_ROUTE } from './shared/constants/routes';
import wrapLazyLoad from './shared/hocs/wrapLazyLoad';
import { useReloadWhenTeamChanges } from './shared/hooks/useReloadWhenTeamChanges';
import { usePermissions } from './shared/providers/permissions.provider';

const FeatureNotEnabledPage = wrapLazyLoad(lazy(() => import('./pages/FeatureNotEnabled/FeatureNotEnabledPage')));
const PageNotFoundPage = wrapLazyLoad(lazy(() => import('./pages/PageNotFound/PageNotFoundPage')));

export default function App() {
  const { isImpersonating } = useAuth();
  const { isReloading: reloadingQueries } = useReloadWhenTeamChanges('/plugins/*', '/plugins');

  const {
    isLoading: loadingPermissions,
    currentTeam,
    isCurrentTeamAdmin,
    totalTeamCount,
    visiblePages,
  } = usePermissions();

  const firstAvailableFeaturePath = visiblePages[0]?.path;

  const isLoading = loadingPermissions || reloadingQueries;
  if (isLoading) {
    return <Loader show fillAndCenter />;
  }

  return (
    <ApplicationFrame>
      {!isCurrentTeamAdmin ? (
        <InsufficientPermissionsPage currentTeamName={currentTeam?.name} teamCount={totalTeamCount} />
      ) : (isImpersonating || isCurrentTeamAdmin) && firstAvailableFeaturePath ? (
        <Routes>
          <Route path={OAUTH_RECTIFIER_ROUTE} element={<OAuthRectifier />} />
          <Route path='/' element={<Navigate replace to={firstAvailableFeaturePath} />} />

          {visiblePages.map(({ path, hasSubPages, Component }) => (
            <Route key={path} path={path + (hasSubPages ? '/*' : '')} element={<Component />} />
          ))}

          <Route path='*' element={<PageNotFoundPage />} />
        </Routes>
      ) : (
        <FeatureNotEnabledPage />
      )}
    </ApplicationFrame>
  );
}
